import { useLayoutEffect, useRef, useState, type MouseEvent } from 'react'
import { tickInterval } from '../alarms/AreaChart'

/**
 * Graficele din networkStatus.php, desenate ca SVG cu tema Highcharts 4:
 * pie-ul de split (innerSize 58%) si spline-urile de 24h. Fara jQuery.
 */

const FONT = '"Lucida Grande", "Lucida Sans Unicode", Verdana, Arial, Helvetica, sans-serif'
export const SPLIT_COLORS = { inbound: '#36a3d9', outbound: '#8e7cc3', other: '#b8c2cf' }
const SERIES_COLORS = ['#7cb5ec', '#434348', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f']

export interface NetworkSeries {
  name: string
  data: number[]
}

export function NetworkPieChart({
  inbound,
  outbound,
  other,
}: {
  inbound: number
  outbound: number
  other: number
}) {
  const total = inbound + outbound + other
  if (total < 1) {
    return (
      <div className="alert alert-info" style={{ margin: 0 }}>
        <i className="fas fa-info-circle"></i> No connection counts reported yet.
      </div>
    )
  }
  const slices = [
    { name: 'Inbound', y: inbound, color: SPLIT_COLORS.inbound },
    { name: 'Outbound', y: outbound, color: SPLIT_COLORS.outbound },
    { name: 'Other states', y: other, color: SPLIT_COLORS.other },
  ].filter((s) => s.y > 0)
  const r = 70
  const ir = r * 0.58
  const cx = 90
  const cy = 90
  let start = -Math.PI / 2
  const arcs = slices.map((s) => {
    const sweep = (s.y / total) * Math.PI * 2
    const end = start + sweep
    const large = sweep > Math.PI ? 1 : 0
    const path =
      slices.length === 1
        ? `M ${cx + r} ${cy} A ${r} ${r} 0 1 1 ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy} ` +
          `M ${cx + ir} ${cy} A ${ir} ${ir} 0 1 0 ${cx - ir} ${cy} A ${ir} ${ir} 0 1 0 ${cx + ir} ${cy}`
        : `M ${cx + Math.cos(start) * r} ${cy + Math.sin(start) * r}` +
          ` A ${r} ${r} 0 ${large} 1 ${cx + Math.cos(end) * r} ${cy + Math.sin(end) * r}` +
          ` L ${cx + Math.cos(end) * ir} ${cy + Math.sin(end) * ir}` +
          ` A ${ir} ${ir} 0 ${large} 0 ${cx + Math.cos(start) * ir} ${cy + Math.sin(start) * ir} Z`
    const mid = start + sweep / 2
    start = end
    return { ...s, path, mid }
  })

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, height: '100%', fontFamily: FONT, fontSize: 11 }}>
      <svg width={180} height={180} viewBox="0 0 180 180" role="img" aria-label="Connections">
        {arcs.map((a) => (
          <path key={a.name} d={a.path} fill={a.color} fillRule="evenodd" />
        ))}
      </svg>
      <div>
        {slices.map((s) => (
          <div key={s.name} style={{ marginBottom: 4, color: '#606060', fontWeight: 'normal' }}>
            <span style={{ color: s.color, marginRight: 6 }}>●</span>
            {s.name}
          </div>
        ))}
      </div>
    </div>
  )
}

export function NetworkSplineChart({
  categories,
  series,
  title,
  yTitle,
  height = 280,
  format,
}: {
  categories: string[]
  series: NetworkSeries[]
  title: string
  yTitle: string
  height?: number
  format: (n: number) => string
}) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  const [hover, setHover] = useState<number | null>(null)

  useLayoutEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setWidth(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const values = series.flatMap((s) => s.data)
  const dataMax = values.length ? Math.max(0, ...values) : 0
  const plotTop = 36
  const plotBottom = height - 40
  const plotHeight = Math.max(1, plotBottom - plotTop)
  const padded = dataMax === 0 ? 1 : dataMax * 1.05
  const step = tickInterval(padded, plotHeight)
  const yMax = Math.max(step, Math.ceil(padded / step) * step)
  const ticks: number[] = []
  for (let v = 0; v <= yMax; v += step) ticks.push(v)
  const plotLeft = 56
  const plotRight = Math.max(plotLeft + 40, width - 12)
  const plotWidth = Math.max(1, plotRight - plotLeft)
  const n = Math.max(1, categories.length)
  const x = (i: number) => plotLeft + (n === 1 ? plotWidth / 2 : (i / (n - 1)) * plotWidth)
  const y = (v: number) => plotBottom - (v / yMax) * plotHeight
  const increment = Math.max(1, Math.ceil((n - 1) / 15))
  const labelIdx = categories.map((_, i) => i).filter((i) => i % increment === 0)

  const onMove = (event: MouseEvent<SVGSVGElement>) => {
    if (!categories.length) return
    const rect = event.currentTarget.getBoundingClientRect()
    const px = event.clientX - rect.left
    let best = 0
    for (let i = 1; i < n; i++) if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i
    setHover(best)
  }

  return (
    <div ref={box} className="highcharts-container" style={{ width: '100%', height, fontFamily: FONT, fontSize: 12 }}>
      {width > 0 && (
        <svg width={width} height={height} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          <rect x={0} y={0} width={width} height={height} fill="#FFFFFF" />
          <text x={width / 2} y={18} textAnchor="middle" style={{ fill: '#333', fontSize: 16, fontFamily: FONT }}>
            {title}
          </text>
          {ticks.map((t) => (
            <g key={t}>
              <path d={`M${plotLeft} ${y(t).toFixed(2)} L${plotRight} ${y(t).toFixed(2)}`} stroke="#D8D8D8" strokeWidth={1} />
              <text x={plotLeft - 8} y={y(t) + 3} textAnchor="end" style={{ fill: '#606060', fontSize: 11, fontFamily: FONT }}>
                {t}
              </text>
            </g>
          ))}
          <path d={`M${plotLeft} ${plotBottom + 0.5} L${plotRight} ${plotBottom + 0.5}`} stroke="#C0D0E0" strokeWidth={1} />
          <text
            transform={`translate(14, ${(plotTop + plotBottom) / 2}) rotate(270)`}
            textAnchor="middle"
            style={{ fill: '#707070', fontSize: 12, fontFamily: FONT }}
          >
            {yTitle}
          </text>
          {series.map((s, si) => {
            const color = SERIES_COLORS[si % SERIES_COLORS.length]!
            const d = s.data
              .map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)} ${y(v).toFixed(2)}`)
              .join(' ')
            return <path key={s.name} d={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
          })}
          {labelIdx.map((i) => (
            <text key={i} x={x(i)} y={plotBottom + 16} textAnchor="middle" style={{ fill: '#606060', fontSize: 11, fontFamily: FONT }}>
              {categories[i]}
            </text>
          ))}
          {series.map((s, si) => (
            <text key={s.name} x={plotLeft + si * 110} y={height - 6} style={{ fill: '#606060', fontSize: 11, fontFamily: FONT }}>
              <tspan style={{ fill: SERIES_COLORS[si % SERIES_COLORS.length] }}>●</tspan> {s.name}
            </text>
          ))}
          {hover !== null && categories[hover] && (
            <g style={{ pointerEvents: 'none' }}>
              <path
                d={`M${x(hover).toFixed(2)} ${plotTop} L${x(hover).toFixed(2)} ${plotBottom}`}
                stroke="#C0D0E0"
                strokeDasharray="3 3"
              />
              {series.map((s, si) => (
                <circle
                  key={s.name}
                  cx={x(hover)}
                  cy={y(s.data[hover] ?? 0)}
                  r={4}
                  fill={SERIES_COLORS[si % SERIES_COLORS.length]}
                  stroke="#fff"
                />
              ))}
              <text x={Math.min(x(hover) + 8, plotRight - 80)} y={plotTop + 12} style={{ fill: '#333', fontSize: 10, fontFamily: FONT }}>
                {categories[hover]}
              </text>
              {series.map((s, si) => (
                <text
                  key={s.name}
                  x={Math.min(x(hover) + 8, plotRight - 80)}
                  y={plotTop + 28 + si * 14}
                  style={{ fill: '#333', fontSize: 11, fontFamily: FONT }}
                >
                  {s.name}: {format(s.data[hover] ?? 0)}
                </text>
              ))}
            </g>
          )}
        </svg>
      )}
    </div>
  )
}

export function lastOf(series: NetworkSeries | undefined): number {
  if (!series || !series.data.length) return 0
  return Number(series.data[series.data.length - 1]) || 0
}

export function bytesLabel(n: number): string {
  let v = Number(n) || 0
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v = v / 1024
    i++
  }
  return (i === 0 ? String(v) : v.toFixed(1)) + ' ' + units[i]
}
